/**
 * useStripeSetupIntent (web) — thin wrapper over the shared
 * `@butter/ui/hooks/payment` hook, injecting web's `paymentMethodService`.
 *
 * See the shared hook for the dedupe/staleness/retry behavior. Kept here so the
 * booking / account-setup / subscription render sites import from
 * `~/features/payment` unchanged.
 *
 * The intent is a SINGLE multi-method SetupIntent — the server resolves its
 * `payment_method_types` (card, or card + bank for the accordion), so web no longer
 * requests a specific method type.
 *
 * @module payment/hooks
 */

import {
  useStripeSetupIntent as useStripeSetupIntentBase,
  type StripeSetupIntentValue,
  type UseStripeSetupIntentResult,
} from '@butter/ui/hooks/payment';
import { paymentMethodService } from '~/services/internalApi/payment/api/paymentMethodService';

/** Back-compat alias for the previously-exported name. */
export type StripeSetupIntent = StripeSetupIntentValue;
export type { UseStripeSetupIntentResult };

/**
 * Fetch a Stripe SetupIntent for `userId` while `enabled` is true.
 *
 * @param userId  - The user (or guest) DB id the instrument is saved for.
 * @param enabled - Only fetch while the Stripe form is active.
 */
export function useStripeSetupIntent(
  userId: number | undefined,
  enabled: boolean
): UseStripeSetupIntentResult {
  return useStripeSetupIntentBase(userId, enabled, {
    createSetupIntent: (id) => paymentMethodService.createSetupIntent(id),
  });
}
